/**
 * Candidate (portal) authentication context.
 *
 * Distinct from the staff `useAuth()` because:
 *   - Different cookie (prism_candidate_session, path=/) vs staff
 *     (prism_staff_session, path=/api/).
 *   - Two-step login = email/password -> OTP code (not TOTP).
 *   - Status is fetched from /portal/status (returns null/401 if no
 *     session), not /api/auth/me.
 *
 * Mounted only at the /portal/* route subtree so the assessor SPA does
 * not pay the extra fetch.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type {
  PortalLoginBeginRequest,
  PortalLoginBeginResponse,
  PortalLoginVerifyRequest,
  PortalStatus,
} from '../types';

interface PortalAuthState {
  status: PortalStatus | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface PortalAuthContextValue extends PortalAuthState {
  loginBegin: (
    payload: PortalLoginBeginRequest
  ) => Promise<PortalLoginBeginResponse>;
  loginVerify: (payload: PortalLoginVerifyRequest) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextValue | null>(null);

interface PortalAuthProviderProps {
  children: ReactNode;
}

export function PortalAuthProvider({ children }: PortalAuthProviderProps) {
  const [state, setState] = useState<PortalAuthState>({
    status: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const refresh = useCallback(async () => {
    try {
      // Note the absolute path: portal endpoints live at /portal/*, not /api/*.
      const resp = await fetch('/portal/status', { credentials: 'include' });
      if (resp.ok) {
        const data = (await resp.json()) as PortalStatus;
        setState({ status: data, isLoading: false, isAuthenticated: true });
      } else {
        setState({ status: null, isLoading: false, isAuthenticated: false });
      }
    } catch {
      setState({ status: null, isLoading: false, isAuthenticated: false });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // When any portalGet/portalPost/portalPatch/portalPostForm sees a 401,
  // it dispatches `prism:portal-401`. We treat that as authoritative
  // session-loss: drop local state so RequirePortalAuth bounces the
  // candidate to /portal/login on the next render. Without this, an
  // expired session leaves the user stuck on a page whose queries
  // silently 401 in the background.
  useEffect(() => {
    function onSessionLost() {
      setState({ status: null, isLoading: false, isAuthenticated: false });
    }
    window.addEventListener('prism:portal-401', onSessionLost);
    return () => window.removeEventListener('prism:portal-401', onSessionLost);
  }, []);

  const loginBegin = useCallback(
    async (payload: PortalLoginBeginRequest) => {
      const resp = await fetch('/portal/login/begin', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const detail =
          (body as { detail?: string }).detail ?? `HTTP ${resp.status}`;
        throw Object.assign(new Error(detail), { status: resp.status });
      }
      return (await resp.json()) as PortalLoginBeginResponse;
    },
    []
  );

  const loginVerify = useCallback(
    async (payload: PortalLoginVerifyRequest) => {
      const resp = await fetch('/portal/login/verify', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const detail =
          (body as { detail?: string }).detail ?? `HTTP ${resp.status}`;
        throw Object.assign(new Error(detail), { status: resp.status });
      }
      await refresh();
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    try {
      await fetch('/portal/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      setState({ status: null, isLoading: false, isAuthenticated: false });
    }
  }, []);

  const value: PortalAuthContextValue = {
    ...state,
    loginBegin,
    loginVerify,
    logout,
    refresh,
  };

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth(): PortalAuthContextValue {
  const ctx = useContext(PortalAuthContext);
  if (ctx === null) {
    throw new Error('usePortalAuth must be used inside PortalAuthProvider');
  }
  return ctx;
}

/** Convert a non-OK Response into a thrown Error with `.status` set,
 *  and broadcast `prism:portal-401` so PortalAuthProvider can drop
 *  local auth state. Centralised so every helper behaves the same.
 *  Skips the broadcast for /portal/status (the auth probe itself —
 *  401 there is the *expected* signed-out signal, not a session loss). */
async function throwPortalError(resp: Response, path: string): Promise<never> {
  const body = await resp.json().catch(() => ({}));
  if (resp.status === 401 && !path.startsWith('/portal/status')) {
    window.dispatchEvent(new CustomEvent('prism:portal-401'));
  }
  // Backend exception handlers return either FastAPI's default
  // `{detail: ...}` shape (Pydantic 422s) or our custom shape
  // `{error, message, field}` (raised PrismError subclasses). Read both
  // so the candidate sees the actual reason instead of "HTTP 422".
  const b = body as { detail?: unknown; message?: string; field?: string };
  const detail =
    typeof b.detail === 'string'
      ? b.detail
      : Array.isArray(b.detail) && b.detail[0]?.msg
        ? String(b.detail[0].msg)
        : null;
  const msg =
    detail ??
    (b.message ? (b.field ? `${b.message} (${b.field})` : b.message) : null) ??
    `HTTP ${resp.status}`;
  throw Object.assign(new Error(msg), { status: resp.status });
}

/** Tiny portal-specific fetch helper that includes credentials on every
 *  call. Used by the session pages instead of the staff `get`/`post` so
 *  the candidate cookie is reliably sent. */
export async function portalGet<T>(path: string): Promise<T> {
  const resp = await fetch(path, { credentials: 'include' });
  if (!resp.ok) await throwPortalError(resp, path);
  return (await resp.json()) as T;
}

export async function portalPost<T>(path: string, body?: unknown): Promise<T> {
  const resp = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) await throwPortalError(resp, path);
  return (await resp.json()) as T;
}

export async function portalPatch<T>(path: string, body?: unknown): Promise<T> {
  const resp = await fetch(path, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) await throwPortalError(resp, path);
  return (await resp.json()) as T;
}

/** Multipart POST. Browser sets Content-Type with boundary itself. */
export async function portalPostForm<T>(path: string, body: FormData): Promise<T> {
  const resp = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    body,
  });
  if (!resp.ok) await throwPortalError(resp, path);
  return (await resp.json()) as T;
}

export async function portalDelete(path: string): Promise<void> {
  const resp = await fetch(path, { method: 'DELETE', credentials: 'include' });
  if (!resp.ok) await throwPortalError(resp, path);
}
